@if (forcedView() === null) {
<ul class="nav nav-tabs mb-3">
  <li class="nav-item">
    <button type="button" class="nav-link" [class.active]="activeTab() === 'commissions'"
      (click)="selectTab('commissions')">Commissions</button>
  </li>
  <li class="nav-item">
    <button type="button" class="nav-link" [class.active]="activeTab() === 'ppj'"
      (click)="selectTab('ppj')">Pay Per Job</button>
  </li>
</ul>
}

@switch (view()) {
  @case ('commissions') {
    <groundworks-card [cardClasses]="['mb-4']" data-testid="commissions-overview-card">
      <groundworks-card-header>
        <ng-template groundworksCardHeaderTitle>
          <h5 class="card-title h5 m-0">Commissions Overview</h5>
          @if (isSelfGen()) {
            <span class="badge bg-info text-dark" data-testid="overview-self-gen-badge">Self-Gen</span>
          }
          @if (anyChangeOrder()) {
            <span class="badge bg-secondary" data-testid="overview-change-order-badge">Change Order</span>
          }
          @if (isInsideSales()) {
            <span class="badge bg-primary" title="Inside Sales Rep" data-testid="overview-isr-badge">ISR</span>
          }
        </ng-template>
      </groundworks-card-header>
      <groundworks-card-body>
        <div class="row text-center g-3" data-testid="commissions-overview">
          <div class="col-6 col-md-3">
            <div class="fw-bold" data-testid="commissions-adjusted-sale">{{ formatCurrency(commissionsAdjustedSale()) }}</div>
            <small class="text-muted">Adj. Sales Amt</small>
          </div>
          <div class="col-6 col-md-3">
            <div class="fw-bold" data-testid="commissions-percent-project-paid">{{ formatRatio(percentProjectPaid() ?? undefined) }}</div>
            <small class="text-muted">% Project Paid</small>
          </div>
          <div class="col-6 col-md-3">
            <div class="fw-bold" data-testid="commissions-discount">{{ formatCurrency(discount()) }}@if (discountPercent() !== null) { ({{ formatRatio(discountPercent() ?? undefined) }})}</div>
            <small class="text-muted">Discount</small>
          </div>
          <div class="col-6 col-md-3">
            <div class="fw-bold text-danger" data-testid="commissions-acr">{{ formatCurrency(acr()) }}</div>
            <small class="text-muted">ACR</small>
          </div>
          <div class="col-6 col-md-3">
            <div class="fw-bold" data-testid="commissions-par-percent">{{ formatRatio2(commissionContext()?.jobParRatio ?? undefined) }}</div>
            <small class="text-muted">PAR %</small>
          </div>
        </div>
      </groundworks-card-body>
    </groundworks-card>

    <div class="d-flex flex-wrap justify-content-end align-items-center gap-2 mb-3">
      @if (canWriteCommissions()) {
        <button type="button" class="btn btn-sm btn-primary" data-testid="add-commission" (click)="openAddCommission()">Add Commission</button>
      }
    </div>

    @if (commissionsError()) {
      <groundworks-state-message
        data-testid="commissions-error"
        type="error"
        message="Could not load commissions. Please try again."
      />
    } @else if (commissions().length === 0) {
      <p class="text-muted mb-0" data-testid="commissions-empty">No commissions recorded.</p>
    } @else {
      <groundworks-card [isCollapsible]="true" [collapsibleVariant]="'box'" [cardClasses]="['mb-4']" data-testid="commissions-card">
        <groundworks-card-header [isCollapsible]="true" [collapsibleVariant]="'box'">
          <ng-template groundworksCardHeaderTitle>
            <h5 class="card-title h5 m-0">Commissions</h5>
          </ng-template>
        </groundworks-card-header>
        <groundworks-card-body>
          @for (table of commissionTables(); track table.sellerId) {
            <div class="border rounded mb-3">
              <groundworks-table
                [attr.data-testid]="'commission-table-' + table.sellerId"
                [data]="table.data"
              />
            </div>
          }
        </groundworks-card-body>
      </groundworks-card>
    }

    @if (commissionApproveError()) {
      <div class="alert alert-danger mb-3" role="alert" data-testid="commission-approve-error">
        {{ commissionApproveError() }}
      </div>
    }

    @if (commissionDeleteError()) {
      <div class="alert alert-danger mb-3" role="alert" data-testid="commission-delete-error">
        {{ commissionDeleteError() }}
      </div>
    }
  }
  @case ('ppj') {
    <groundworks-card [cardClasses]="['mb-4']" data-testid="ppj-overview-card">
      <groundworks-card-header>
        <ng-template groundworksCardHeaderTitle>
          <h5 class="card-title h5 m-0">PPJ Overview</h5>
        </ng-template>
      </groundworks-card-header>
      <groundworks-card-body>
        <div class="row text-center g-3">
          <div class="col-6 col-md-3">
            <div class="fw-bold" data-testid="ppj-adjusted-sale">{{ formatCurrency(ppj()?.summary?.adjustedSale) }}</div>
            <small class="text-muted">Adj. Sales Amt</small>
          </div>
          <div class="col-6 col-md-3">
            <div class="fw-bold" data-testid="ppj-percent-project-paid">{{ formatRatio(percentProjectPaid() ?? undefined) }}</div>
            <small class="text-muted">% of Job Paid</small>
          </div>
          <div class="col-6 col-md-3">
            <div class="fw-bold" data-testid="ppj-actual-labor">{{ formatCurrency(ppj()?.summary?.actualLaborCost) }}</div>
            <small class="text-muted">Actual Labor</small>
          </div>
          <div class="col-6 col-md-3">
            <div class="fw-bold text-danger" data-testid="ppj-acr">{{ formatCurrency(acr()) }}</div>
            <small class="text-muted">ACR</small>
          </div>
        </div>
      </groundworks-card-body>
    </groundworks-card>

    @if (ppj()?.summary?.amountPaidExceedsPot === true) {
      <div class="alert alert-warning mb-4" role="alert" data-testid="ppj-exceeds-pot-warning">
        Payouts exceed the current bonus pot.
      </div>
    }

    <groundworks-card [isCollapsible]="true" [collapsibleVariant]="'box'" [cardClasses]="['mb-4']" data-testid="ppj-bonus-card">
      <groundworks-card-header [isCollapsible]="true" [collapsibleVariant]="'box'">
        <ng-template groundworksCardHeaderTitle>
          <h5 class="card-title h5 m-0">PPJ Bonus</h5>
        </ng-template>
      </groundworks-card-header>
      <groundworks-card-body>
        <div class="mb-3">
          <app-ppj-budget-form
            [projectId]="projectId()"
            [bonus]="ppj()?.bonus ?? null"
            [defaultBonusPot]="ppj()?.defaultBonusPot ?? null"
            [adjustedSale]="ppjAdjustedSale()"
            [actualLaborCost]="ppjActualLaborCost()"
            [locked]="hasPpjPayments()"
            [canEdit]="canWritePpj()"
            (saved)="onPpjBudgetSaved()"
          />
        </div>

        @if (ppjApproveError()) {
          <div class="alert alert-danger mb-3" role="alert" data-testid="ppj-approve-error">
            {{ ppjApproveError() }}
          </div>
        }

        @if (ppjError()) {
          <groundworks-state-message
            data-testid="ppj-error"
            type="error"
            message="Could not load PPJ data. Please try again."
          />
        } @else if (employees().length === 0) {
          <groundworks-state-message
            data-testid="ppj-employees-empty"
            type="no-matches"
            message="No employee bonus data available."
            customIcon="bi-people"
          />
        } @else {
          <div class="table-responsive mt-3">
            <groundworks-table data-testid="ppj-employees-table" [data]="ppjEmployeeTableData()" />
          </div>
        }

      </groundworks-card-body>
    </groundworks-card>

    <!-- D11 — its own card, a sibling BELOW the PPJ Bonus card rather than nested inside it. An
         exception never touches the bonus pool, and nesting it under a heading that says "PPJ Bonus"
         contradicted that in the one place users see. The split also gives it its own
         error/empty/table chain, so either card can fail without blanking the other. -->
    <groundworks-card [isCollapsible]="true" [collapsibleVariant]="'box'" [cardClasses]="['mb-4']" data-testid="ppj-exceptions-card">
      <groundworks-card-header [isCollapsible]="true" [collapsibleVariant]="'box'">
        <ng-template groundworksCardHeaderTitle>
          <h5 class="card-title h5 m-0">PPJ Exceptions</h5>
        </ng-template>
      </groundworks-card-header>
      <groundworks-card-body>
        @if (canWritePpj()) {
          <div class="d-flex justify-content-end mb-3">
            <button type="button" class="btn btn-sm btn-primary" data-testid="add-ppj-exception"
              (click)="openAddPpjException()">Add Exception</button>
          </div>
        }

        @if (ppjExceptionActionError()) {
          <div class="alert alert-danger" role="alert" data-testid="ppj-exception-action-error">
            {{ ppjExceptionActionError() }}
          </div>
        }

        @if (ppjExceptionsError()) {
          <groundworks-state-message
            data-testid="ppj-exceptions-error"
            type="error"
            message="Could not load PPJ exceptions. Please try again."
          />
        } @else if (ppjExceptions().length === 0) {
          <groundworks-state-message
            data-testid="ppj-exceptions-empty"
            type="no-matches"
            message="No PPJ exceptions recorded."
            customIcon="bi-cash-stack"
          />
        } @else {
          <div class="table-responsive">
            <groundworks-table data-testid="ppj-exceptions-table" [data]="ppjExceptionTableData()" />
          </div>
        }
      </groundworks-card-body>
    </groundworks-card>

    @if (ppjPaidError()) {
      <div class="alert alert-danger mb-3" role="alert" data-testid="ppj-paid-error">
        {{ ppjPaidError() }}
      </div>
    }
  }
}
